import React, { useEffect, useMemo, useState } from "react";
import { Users, Search, ChevronRight } from "lucide-react";
import { api, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FileDown } from "lucide-react";
import { Badge2 } from "@/components/ComplianceBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function validityInfo(vu, status) {
  if (status === "Vencido") return { color: "red", expired: true };
  if (!vu) return { color: "slate", expired: false };
  const days = Math.round((new Date(vu) - new Date()) / 86400000);
  if (days < 0) return { color: "red", expired: true };
  if (days <= 30) return { color: "yellow", expired: false };
  return { color: "green", expired: false };
}

export default function TrainingsByEmployee({ headerExtra }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);

  useEffect(() => {
    api.get("/trainings").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const groups = useMemo(() => {
    const m = {};
    rows.forEach((r) => { const k = (r.employee_name || "—").trim(); (m[k] = m[k] || []).push(r); });
    return Object.entries(m).map(([name, list]) => {
      let valid = 0, expired = 0;
      list.forEach((t) => { validityInfo(t.valid_until, t.status).expired ? expired++ : valid++; });
      return { name, list, total: list.length, valid, expired };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const filtered = groups.filter((g) => g.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="trainings-by-employee">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-[#0B2545]">Treinamentos por Colaborador</h2>
          <p className="text-sm text-slate-500">{groups.length} colaborador(es) · clique em um nome para ver treinamentos e validades</p>
        </div>
        <div className="flex items-center gap-2">{headerExtra}</div>
      </div>
      <div className="relative mb-3 max-w-sm">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
        <Input className="pl-9" placeholder="Buscar colaborador..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="tbe-search" />
      </div>
      {loading ? <p className="text-sm text-slate-400">Carregando...</p> : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((g) => (
            <button key={g.name} onClick={() => setSel(g)} className="flex items-center justify-between rounded-xl border border-[#E2E8F0] bg-white p-4 text-left shadow-sm transition hover:border-[#134074]/40 hover:shadow" data-testid={`tbe-emp-${g.name}`}>
              <div>
                <p className="text-sm font-bold text-[#0B2545]">{g.name}</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <Badge2 color="green">{g.valid} válidos</Badge2>
                  {g.expired > 0 && <Badge2 color="red">{g.expired} vencidos</Badge2>}
                </div>
              </div>
              <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />
            </button>
          ))}
          {filtered.length === 0 && <p className="text-sm text-slate-400">Nenhum colaborador encontrado.</p>}
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[720px]" data-testid="tbe-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-[#0B2545]"><Users className="h-5 w-5" />{sel?.name}</DialogTitle></DialogHeader>
          {sel && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-slate-500">{sel.total} treinamento(s) · {sel.valid} válido(s) · {sel.expired} vencido(s)</p>
              {(() => { const eid = sel.list.find((t) => t.employee_id)?.employee_id; return eid ? (
                <Button size="sm" variant="outline" onClick={() => window.open(`${API}/trainings/ficha/${eid}/pdf`, "_blank")} className="border-[#0B2545]/30 text-[#0B2545] hover:bg-[#0B2545]/5" data-testid="tbe-ficha-btn">
                  <FileDown className="mr-1.5 h-4 w-4" /> Exportar Ficha (PDF)
                </Button>
              ) : null; })()}
            </div>
          )}
          <div className="mt-2 overflow-hidden rounded-xl border border-[#E2E8F0]">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">Treinamento</th><th className="px-3 py-2">Data</th><th className="px-3 py-2">Validade</th><th className="px-3 py-2">Carga</th>
              </tr></thead>
              <tbody>
                {sel && [...sel.list].sort((a, b) => {
                  const ea = validityInfo(a.valid_until, a.status).expired ? 0 : 1;
                  const eb = validityInfo(b.valid_until, b.status).expired ? 0 : 1;
                  return ea - eb || String(a.topic).localeCompare(String(b.topic));
                }).map((t) => {
                  const vi = validityInfo(t.valid_until, t.status);
                  return (
                    <tr key={t.id} className="border-b border-slate-100 last:border-0">
                      <td className="px-3 py-2 font-medium text-slate-700">{t.topic}</td>
                      <td className="px-3 py-2 text-slate-500">{t.date || "—"}</td>
                      <td className="px-3 py-2"><Badge2 color={vi.color}>{t.valid_until || "sem validade"}</Badge2></td>
                      <td className="px-3 py-2 text-slate-500">{t.workload_h ? `${t.workload_h}h` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
